import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Chatv1Error, getRoomBookmarks, setRoomBookmark } from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { chatv1SelfEmail } from '../chatv1/auth'
import { parseFileCard, type Bookmark, type ProjectMessage } from '../transport/api'
import { wireToProjectMessage } from './projectDynamo'

/**
 * A PROJECT'S BOOKMARKS, THE VIEWER'S OWN — owner, 2026-11-01: 我們在 project 裡面也可以加入書籤, 這樣使用者就可以把某一個
 * 其他使用者或是 agent 的會話給釘在 project 裡面. 這個書籤就只有該使用者自己可以看到.
 *
 * One list per room, kept by the chat service under the person (chat lambda/bookmarks.ts) and answered to them alone —
 * so the buttons under the room's messages, the count on the corner button and the list in the drawer are one query,
 * and what was kept on the phone is the desktop's too: their other devices are told (a `bookmark` frame) and re-read,
 * and every socket (re)open re-reads, since a frame missed while it was down never arrives late.
 *
 * A chat service from before bookmarks has no such route (the gateway's bare 404): then `supported` is false and
 * nothing offers a bookmark — a button that can only fail is worse than none.
 */
export type RoomBookmark = {
  seq: number
  /** When it was kept (ms). */
  ts: number
  /** The message as it now is; null when it is gone from the room. */
  message: ProjectMessage | null
  /** Drawn before the service answered. */
  pending?: true
}

const roomBookmarksKey = (conv: string | null) => ['room-bookmarks', conv ?? ''] as const
/** The name a jump to a room's message goes by (agents/bookmarks/jump.ts): rooms and agents share the one channel. */
export const roomJumpSession = (conv: string) => `room:${conv}`

/** The service has no bookmarks route — the gateway's own 404, not the handler's. */
export function isOldService(e: unknown): boolean {
  return e instanceof Chatv1Error && e.status === 404 && e.code === 'http_404'
}

/** What a refusal says to the person, in words. */
function refusalText(e: unknown): string {
  const code = e instanceof Chatv1Error ? e.code : ''
  if (code === 'too_many_bookmarks') return 'You can keep at most 300 messages in one project. Remove one first.'
  if (code === 'no_such_message') return 'That message is no longer in this project.'
  if (code === 'not_a_member') return 'You are no longer in this project.'
  return 'That bookmark could not be saved. Try again in a moment.'
}

/** One room's list as a query — the room's and More › Bookmarks' (shell/BookmarksPage), one cache entry. */
export function roomBookmarksQuery(conv: string | null) {
  return {
    queryKey: roomBookmarksKey(conv),
    queryFn: async (): Promise<RoomBookmark[]> => {
      const self = chatv1SelfEmail() || null
      const r = await getRoomBookmarks(conv!)
      return (r.bookmarks ?? []).map((b) => ({
        seq: b.seq,
        ts: b.ts,
        message: b.message ? wireToProjectMessage(b.message, self) : null,
      }))
    },
    enabled: !!conv,
    staleTime: 15_000,
    retry: (n: number, e: unknown) => !isOldService(e) && n < 2,
  }
}

export function useRoomBookmarks(conv: string | null) {
  const qc = useQueryClient()
  const key = roomBookmarksKey(conv)
  const q = useQuery(roomBookmarksQuery(conv))

  // Another device kept or let go of one, or the socket came back: read the list again.
  useEffect(() => {
    if (!conv) return
    const reread = () => void qc.invalidateQueries({ queryKey: roomBookmarksKey(conv) })
    const offFrame = onChatv1Frame((f) => {
      if (f.type === 'bookmark' && f.conv_id === conv) reread()
    })
    const offOpen = onChatv1Open(reread)
    return () => {
      offFrame()
      offOpen()
    }
  }, [conv, qc])

  /** The last refusal, and the message it was about — said under that message. */
  const [error, setError] = useState<{ seq: number; text: string } | null>(null)
  const toggle = useMutation({
    mutationFn: ({ seq, on }: { seq: number; on: boolean; m?: ProjectMessage }) => setRoomBookmark(conv!, seq, on),
    // Drawn at once; the service's answer replaces it (or, refused, takes it back).
    onMutate: async ({ seq, on, m }) => {
      setError(null)
      await qc.cancelQueries({ queryKey: key })
      const before = qc.getQueryData<RoomBookmark[]>(key)
      const rest = (before ?? []).filter((b) => b.seq !== seq)
      qc.setQueryData<RoomBookmark[]>(key, on ? [{ seq, ts: Date.now(), message: m ?? null, pending: true }, ...rest] : rest)
      return { before }
    },
    onError: (e, { seq }, ctx) => {
      qc.setQueryData(key, ctx?.before)
      setError({ seq, text: refusalText(e) })
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  })

  const list = q.data ?? []
  return {
    list,
    /** Known to work: the list has been read (a later failed re-read does not take the buttons away). */
    supported: q.data !== undefined,
    loading: q.isLoading,
    error: q.error && !isOldService(q.error) ? q.error : null,
    refetch: () => void q.refetch(),
    /** Kept, being kept, or not. */
    stateOf: (seq: number): 'kept' | 'pending' | null => {
      const b = list.find((x) => x.seq === seq)
      return b ? (b.pending ? 'pending' : 'kept') : null
    },
    keep: (m: ProjectMessage) => toggle.mutate({ seq: m.cursor, on: true, m }),
    letGo: (seq: number) => toggle.mutate({ seq, on: false }),
    writeError: error,
  }
}

/**
 * The tags a message carries as the words they stand for — an agent's address as @its name, a file shared inline as its
 * name — the way the room draws them (chat/Markdown.tsx), not as markup in the list.
 */
function plainWords(text: string): string {
  const at = (hash: string, label?: string) => `@${label?.trim() || hash.slice(0, 8)}`
  return text
    .replace(/<ping-agent:(fr_[0-9a-f]{12}|[0-9a-f]+)(?::([^>]*))?>/gi, (_m, h: string, l?: string) => at(h, l))
    .replace(/\bping-agent:(fr_[0-9a-f]{12}|[0-9a-f]+)(?::([^\s<>]*))?/gi, (_m, h: string, l?: string) => at(h, l))
    .replace(/<chat-file:[0-9a-f-]+(?::([^>]*))?>/gi, (_m, name?: string) => name?.trim() || 'a file')
}

/** A message's words for the list: its text, a file's caption or its files' names. */
function wordsOf(m: ProjectMessage): string {
  const card = parseFileCard(m.body)
  if (!card) return plainWords(m.body ?? '')
  const names = (card.attachments ?? []).map((a) => a.name).filter(Boolean)
  return plainWords((card.text ?? '').trim()) || names.join(', ') || '(an attachment)'
}

/**
 * A kept message as the drawer's list draws it (agents/bookmarks/BookmarkPanel): the room is its "file", the message's
 * seq its index, and its id is the seq — what letting go of it names.
 */
export function toPanelRow(conv: string, b: RoomBookmark): Bookmark {
  const m = b.message
  const agent = typeof m?.from_ref?.agent === 'string' ? m.from_ref.agent : null
  return {
    id: b.pending ? `pending:${b.seq}` : String(b.seq),
    host: null,
    session: conv,
    file: conv,
    index: b.seq,
    ts: m ? m.ts : null,
    who: m ? (m.from_ref?.label ?? m.from_ref?.email ?? 'someone') : 'Removed',
    kind: !m ? 'inbound' : agent ? 'agent' : m['mine'] === true ? 'you' : 'inbound',
    text: m ? wordsOf(m) : 'This message is no longer in the project.',
    created_ts: Math.floor(b.ts / 1000),
  }
}
